import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseJsonField } from '../utils';

/**
 * The two state shapes every lambda-ish scaffolder field extension needs.
 *
 * Seven adapters had their own copy of this, differing only in the type parameter and which
 * config-editor component they rendered. The copies had already drifted: three compared formData by
 * reference and three by JSON value (the reference form re-runs whenever rjsf hands back an equal
 * object with a new identity), and the "is this object empty?" test disagreed about whether an empty
 * array counts as a value. These hooks settle on the value-compare and the stricter emptiness test.
 */

/**
 * The slice of rjsf's FieldExtensionComponentProps these hooks read. `uiSchema` and `formContext`
 * stay loose on purpose: rjsf's own UiSchema is a wide intersection type, so narrowing them here
 * makes every adapter's concrete Props fail to assign.
 */
type FieldLike<T> = {
  onChange: (value: T) => void;
  schema: { default?: any; title?: string; description?: string };
  uiSchema?: any;
  formData?: T;
  formContext?: unknown;
};

/** Does this parsed object hold anything a user would call a value? Empty string/array do not count. */
function hasMeaningfulValues(parsed: object): boolean {
  return (
    Object.keys(parsed).length > 0 &&
    Object.values(parsed).some(
      v => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0),
    )
  );
}

/**
 * A `Record<string, T>` field (a map of lambda name -> config).
 *
 * `schema.default` seeds the field ONLY on the first render and ONLY while the user has not
 * touched it — otherwise deleting the last lambda would immediately repopulate from the template
 * default, which reads as the form refusing the edit.
 */
export function useRecordField<T>(props: FieldLike<Record<string, T>>) {
  const { onChange, schema, uiSchema, formData, formContext } = props;
  const defaultsFieldName = uiSchema?.['ui:options']?.defaultsFieldName;
  const lastSerializedRef = useRef<string>(JSON.stringify(formData));
  const hasUserInteractedRef = useRef(false);

  const rootFormData = (formContext as any)?.formData as Record<string, unknown> | undefined;

  const getInitialValue = useCallback(
    (isInitialLoad: boolean): Record<string, T> => {
      const fromSchemaDefault = () =>
        schema.default !== undefined
          ? parseJsonField(schema.default, {} as Record<string, T>)
          : ({} as Record<string, T>);

      if (formData === undefined || formData === null) return fromSchemaDefault();

      const parsed = parseJsonField(formData, {} as Record<string, T>);
      const isEmpty = Object.keys(parsed).length === 0;
      if (isEmpty && isInitialLoad && !hasUserInteractedRef.current) return fromSchemaDefault();
      return parsed;
    },
    [formData, schema],
  );

  const [items, setItems] = useState<Record<string, T>>(() => getInitialValue(true));

  useEffect(() => {
    const serialized = JSON.stringify(formData);
    if (serialized === lastSerializedRef.current) return;
    lastSerializedRef.current = serialized;
    setItems(getInitialValue(!hasUserInteractedRef.current));
  }, [formData, getInitialValue]);

  /** Sibling field holding the group defaults, named by `ui:options.defaultsFieldName`. */
  const defaults = useMemo(() => {
    if (!formContext || !defaultsFieldName) return undefined;
    const raw = rootFormData?.[defaultsFieldName];
    return raw ? parseJsonField<any>(raw, undefined) : undefined;
  }, [formContext, defaultsFieldName, rootFormData]);

  const commit = useCallback(
    (next: Record<string, T>) => {
      hasUserInteractedRef.current = true;
      setItems(next);
      onChange(next);
    },
    [onChange],
  );

  return { items, defaults, rootFormData, commit };
}

/**
 * A single-object field (a `defaults` block).
 *
 * When formData holds nothing meaningful the field adopts `fallback` AND pushes it up via onChange,
 * so the template's rendered output carries the same values the form is showing.
 */
export function useObjectField<T extends object>(
  props: FieldLike<T>,
  opts: { fallback: () => T; sanitize?: (parsed: T) => T },
) {
  const { onChange, formData } = props;

  // The callbacks and onChange arrive as fresh identities on every rjsf render. Hold them in refs
  // so the sync effect below keys on the DATA only — depending on their identity re-ran the effect
  // every render, and because the empty branch calls onChange that was an unbounded loop.
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const resolve = useCallback((data: T | undefined): T => {
    const { fallback, sanitize } = optsRef.current;
    if (data !== undefined && data !== null) {
      const parsed = parseJsonField(data, {} as T);
      if (hasMeaningfulValues(parsed)) return sanitize ? sanitize(parsed) : parsed;
    }
    return fallback();
  }, []);

  const [value, setValue] = useState<T>(() => resolve(formData));
  const serialized = JSON.stringify(formData);
  // A separate "has run" flag, not a sentinel value: JSON.stringify(undefined) IS undefined, so a
  // ref initialised to undefined compares equal on the very first render when the field has no
  // value yet — the effect returned early and the fallback was displayed but never published into
  // the scaffolder form data that renders the template.
  const lastSerializedRef = useRef<string | undefined>(undefined);
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (hasSyncedRef.current && serialized === lastSerializedRef.current) return;
    hasSyncedRef.current = true;
    lastSerializedRef.current = serialized;
    const resolved = resolve(formData);
    setValue(resolved);
    // Empty formData means the template rendered nothing here, so publish what the form displays.
    if (!formData || Object.keys(formData).length === 0) onChangeRef.current(resolved);
    // formData is covered by `serialized`; adding it would key on identity again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, resolve]);

  const commit = useCallback((next: T) => {
    setValue(next);
    onChangeRef.current(next);
  }, []);

  return { value, commit };
}
